import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BookingDialog } from "@/components/BookingDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CARGO_LABELS, formatEUR, formatDate, formatCo2 } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, ArrowRight, Calendar, Truck, BadgeCheck, Split, ShieldCheck, Package, ArrowLeft, Leaf } from "lucide-react";

export default function RouteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [route, setRoute] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/routes/${id}`).then(({ data }) => setRoute(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return (
    <div className="App bg-background min-h-screen"><Navbar />
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <p className="font-display text-2xl font-bold">Tratta non trovata</p>
        <Button className="rounded-sm mt-6" onClick={() => navigate("/cerca")}>Torna alla ricerca</Button>
      </div><Footer />
    </div>
  );

  if (!route) return <div className="App bg-background min-h-screen"><Navbar /><div className="p-24 text-center text-muted-foreground">Caricamento…</div></div>;

  const timeline = [route.origin, ...(route.stops || []), route.destination];
  const canBook = user && user.role !== "admin" && user.user_id !== route.carrier_id;

  return (
    <div className="App bg-background min-h-screen">
      <Navbar />
      <div className="mx-auto max-w-5xl px-6 py-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-btn"><ArrowLeft size={16} /> Indietro</button>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                {route.partial_available && <Badge className="bg-accent text-accent-foreground rounded-sm gap-1"><Split size={12} /> Trasporto parziale</Badge>}
                {route.carrier_verified && <Badge variant="outline" className="rounded-sm gap-1 border-primary text-primary"><BadgeCheck size={12} /> Verificato</Badge>}
              </div>
              <h1 className="font-display font-extrabold tracking-tighter text-3xl md:text-4xl mt-4 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-2"><MapPin size={24} className="text-primary" />{route.origin}</span>
                <ArrowRight className="text-accent" />
                <span>{route.destination}</span>
              </h1>
            </div>

            {/* timeline */}
            <div className="border border-border rounded-sm p-6">
              <span className="label-eyebrow text-muted-foreground">Itinerario</span>
              <div className="mt-4 space-y-0">
                {timeline.map((c, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${i === 0 ? "bg-primary" : i === timeline.length - 1 ? "bg-accent" : "bg-muted-foreground"}`} />
                      {i < timeline.length - 1 && <div className="w-px flex-1 bg-border my-1" style={{ minHeight: 24 }} />}
                    </div>
                    <div className="pb-4">
                      <p className="font-display font-semibold">{c}</p>
                      <p className="text-xs text-muted-foreground">{i === 0 ? "Partenza" : i === timeline.length - 1 ? "Arrivo" : "Tappa intermedia"}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="label-eyebrow text-muted-foreground">Merce trasportabile</span>
              <div className="flex flex-wrap gap-2 mt-3">
                {route.cargo_types?.map((c) => (
                  <span key={c} className="text-sm px-3 py-1.5 bg-muted rounded-sm border border-border flex items-center gap-1.5"><Package size={14} /> {CARGO_LABELS[c] || c}</span>
                ))}
              </div>
            </div>

            {route.notes && (
              <div>
                <span className="label-eyebrow text-muted-foreground">Note del trasportatore</span>
                <p className="text-sm mt-2 text-muted-foreground leading-relaxed">{route.notes}</p>
              </div>
            )}
          </div>

          {/* sidebar */}
          <aside className="lg:col-span-1">
            <div className="border border-border rounded-sm p-6 lg:sticky lg:top-24 space-y-4">
              <div>
                <span className="label-eyebrow text-muted-foreground">Range di prezzo</span>
                <p className="font-display font-extrabold text-2xl text-primary mt-1">
                  {formatEUR(route.price_min)}{route.price_max > route.price_min ? ` – ${formatEUR(route.price_max)}` : ""}
                </p>
              </div>
              <InfoRow icon={Calendar} label="Partenza" value={formatDate(route.departure_date)} />
              {route.arrival_date && <InfoRow icon={Calendar} label="Arrivo previsto" value={formatDate(route.arrival_date)} />}
              {route.vehicle_type && <InfoRow icon={Truck} label="Mezzo" value={route.vehicle_type} />}
              {route.distance_km > 0 && <InfoRow icon={ArrowRight} label="Distanza" value={`${route.distance_km} km`} />}
              {route.capacity_note && <InfoRow icon={Package} label="Capacità" value={route.capacity_note} />}

              {route.co2_saved_kg > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="route-co2-box">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <Leaf size={16} />
                    <span className="label-eyebrow">Impatto ambientale</span>
                  </div>
                  <p className="font-display font-extrabold text-2xl text-emerald-700 mt-1">{formatCo2(route.co2_saved_kg)}</p>
                  <p className="text-xs text-emerald-700/80">di CO₂ risparmiata riempiendo questo viaggio a vuoto.</p>
                </div>
              )}
              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium">{route.carrier_name}</p>
                <p className="text-xs text-muted-foreground">Trasportatore</p>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-sm p-3 flex items-start gap-2">
                <ShieldCheck size={16} className="text-primary mt-0.5 shrink-0" />
                <p className="text-xs text-muted-foreground">Assicura la merce al <strong className="text-foreground">3%</strong> del valore in fase di richiesta.</p>
              </div>

              {canBook ? (
                <BookingDialog route={route} user={user} trigger={
                  <Button className="w-full rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="open-booking-btn">Richiedi trasporto</Button>
                } />
              ) : !user ? (
                <Button onClick={() => navigate("/accedi")} className="w-full rounded-sm bg-accent text-accent-foreground font-semibold" data-testid="login-to-book-btn">Accedi per prenotare</Button>
              ) : (
                <p className="text-xs text-center text-muted-foreground">Questa è una tua tratta o sei un amministratore.</p>
              )}
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-muted-foreground"><Icon size={15} /> {label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
